import { IconChevronRight } from '@tvg/fuse-icons';
import PropTypes from 'prop-types';
import React from 'react';
import { Link } from '../../fuse-link/src/links';
import { Picture } from '../../fuse-picture/src/picture';
import {
	StyledBody,
	StyledContentCard,
	StyledHeading,
	StyledImageWrapper,
	StyledLegalText,
	StyledLinksWrapper,
	StyledSpanLink,
	StyledVideoWrapper,
} from './styled-content-card';

const defaultProps = {
	aspectRatio: '1:1',
	body: undefined,
	className: 'fuse-content-card',
	dataTestId: 'fuse-content-card-test-id',
	dataAnalyticsId: undefined,
	dataAnalyticsIdSecondary: undefined,
	headerTag: 'h3',
	heading: undefined,
	isLazy: true,
	legalText: undefined,
	link: {
		href: undefined,
		text: undefined,
	},
	removeMargin: false,
	secondaryLink: {
		href: undefined,
		text: undefined,
	},
	image: {
		fallbackSource: undefined,
		sources: undefined,
		alt: undefined,
	},
	videoPlayerSlot: undefined,
};

const displayName = 'ContentCard';

const propTypes = {
	aspectRatio: PropTypes.oneOf(['1:1', '16:9', '4:3', '5:4', '3:4']),
	body: PropTypes.string,
	className: PropTypes.string,
	dataAnalyticsId: PropTypes.string,
	dataAnalyticsIdSecondary: PropTypes.string,
	dataTestId: PropTypes.string,
	headerTag: PropTypes.string,
	heading: PropTypes.string,
	image: PropTypes.shape({
		fallbackSource: PropTypes.string,
		sources: PropTypes.arrayOf(
			PropTypes.shape({
				srcSet: PropTypes.string,
				type: PropTypes.string,
			}),
		),
		alt: PropTypes.string,
	}),
	isLazy: PropTypes.bool,
	legalText: PropTypes.string,
	link: PropTypes.shape({
		href: PropTypes.string,
		text: PropTypes.string,
	}),
	removeMargin: PropTypes.bool,
	secondaryLink: PropTypes.shape({
		href: PropTypes.string,
		text: PropTypes.string,
	}),
	videoPlayerSlot: PropTypes.node,
};

function ContentCard({
	aspectRatio,
	body,
	className,
	dataAnalyticsId,
	dataAnalyticsIdSecondary,
	dataTestId,
	headerTag,
	heading,
	image,
	isLazy,
	legalText,
	link,
	removeMargin,
	secondaryLink,
	videoPlayerSlot,
	...props
}) {
	const activePrimaryLink = link.href;
	const activePrimaryLinkAndText = link.href && link.text;
	const activeSecondaryLinkAndText = secondaryLink.href && secondaryLink.text;
	const activeVideo = videoPlayerSlot;

	return (
		<StyledContentCard
			className={className}
			as={!activePrimaryLink || activeVideo ? 'div' : activeSecondaryLinkAndText && 'div'}
			data-analyticsid={activePrimaryLink && !activeSecondaryLinkAndText && dataAnalyticsId}
			data-testid={dataTestId}
			href={activePrimaryLink && !activeSecondaryLinkAndText && !activeVideo && link.href}
			removeMargin={removeMargin}
			{...props}
		>
			{videoPlayerSlot && <StyledVideoWrapper>{videoPlayerSlot}</StyledVideoWrapper>}

			{image && !videoPlayerSlot && (
				<StyledImageWrapper aspectRatio={aspectRatio}>
					<Picture {...image} isLazy={isLazy} />
				</StyledImageWrapper>
			)}

			{heading && (
				<StyledHeading
					body={body}
					type="heading"
					tag={headerTag}
					size={{ xxs: '1', s: '2', l: '3' }}
				>
					{heading}
				</StyledHeading>
			)}
			{body && (
				<StyledBody type="paragraph" size="1">
					{body}
				</StyledBody>
			)}

			{activePrimaryLinkAndText && !activeSecondaryLinkAndText && !activeVideo && (
				<StyledSpanLink {...link} iconRight icon={<IconChevronRight />}>
					{link.text}
				</StyledSpanLink>
			)}

			{activePrimaryLinkAndText && (activeSecondaryLinkAndText || activeVideo) && (
				<StyledLinksWrapper>
					<Link
						{...link}
						className="fuse-content-card__primary-link"
						data-testid="fuse-content-card-primary-link-test-id"
						href={link.href}
						iconRight
						icon={<IconChevronRight />}
						data-analyticsid={dataAnalyticsId}
					>
						{link.text}
					</Link>

					{activeSecondaryLinkAndText && (
						<Link
							{...secondaryLink}
							className="fuse-content-card__secondary-link"
							data-testid="fuse-content-card-secondary-link-test-id"
							href={secondaryLink.href}
							iconRight
							icon={<IconChevronRight />}
							data-analyticsid={dataAnalyticsIdSecondary}
						>
							{secondaryLink.text}
						</Link>
					)}
				</StyledLinksWrapper>
			)}

			{legalText && (
				<StyledLegalText type="paragraph" size={{ xxs: 'm2' }}>
					{legalText}
				</StyledLegalText>
			)}
		</StyledContentCard>
	);
}

ContentCard.propTypes = propTypes;
ContentCard.displayName = displayName;
ContentCard.defaultProps = defaultProps;

export { ContentCard };
export default ContentCard;
